import React, { useState, useMemo, useEffect } from 'react';
import { getInsumos, createInsumo, updateInsumo, deleteInsumo, entradaInsumo } from '../api';

function fmt(v) {
  return Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/* ── Formulário de criação com preço embutido ───────────────────────────── */
function InsumoForm({ onSubmit, onCancel, saving }) {
  const [nome,     setNome]     = useState('');
  const [unidade,  setUnidade]  = useState('g');
  const [peso,     setPeso]     = useState('');
  const [preco,    setPreco]    = useState('');

  const custoUnit = peso && preco && Number(peso) > 0
    ? Number(preco) / Number(peso)
    : null;

  const handleSubmit = e => {
    e.preventDefault();
    if (!nome.trim() || !peso || !preco) return;
    onSubmit({ nome: nome.trim(), unidade, peso_embalagem: Number(peso), preco_pago: Number(preco) });
  };

  return (
    <form onSubmit={handleSubmit}>
      <input
        className="inp"
        placeholder="Nome do ingrediente  (ex: Frango peito) *"
        value={nome}
        onChange={e => setNome(e.target.value)}
        required autoFocus
        style={{ marginBottom: 8 }}
      />

      <div style={{ fontSize: 12, color: 'var(--c4)', marginBottom: 4 }}>Unidade de medida</div>
      <select className="inp inp-sel" value={unidade} onChange={e => setUnidade(e.target.value)} style={{ marginBottom: 12 }}>
        <option value="g">g — gramas (carnes, queijos, farinhas…)</option>
        <option value="ml">ml — mililitros (óleos, molhos, leite…)</option>
        <option value="un">un — unidade (ovos, sachês, latas…)</option>
      </select>

      <div style={{ fontSize: 12, color: 'var(--c4)', marginBottom: 6 }}>
        Quanto você pagou na última compra?
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 11, color: 'var(--c4)', display: 'block', marginBottom: 3 }}>
            Peso da embalagem ({unidade})
          </label>
          <input
            className="inp"
            type="number" step="0.001" min="0.001" required
            placeholder={unidade === 'g' ? 'Ex: 1000' : unidade === 'ml' ? 'Ex: 900' : 'Ex: 12'}
            value={peso}
            onChange={e => setPeso(e.target.value)}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 11, color: 'var(--c4)', display: 'block', marginBottom: 3 }}>
            Preço pago (R$)
          </label>
          <input
            className="inp"
            type="number" step="0.01" min="0.01" required
            placeholder="Ex: 12,90"
            value={preco}
            onChange={e => setPreco(e.target.value)}
          />
        </div>
      </div>

      {custoUnit !== null && (
        <div style={{ padding: '8px 12px', background: 'var(--g4)', borderRadius: 10, fontSize: 13, marginBottom: 12 }}>
          Custo por {unidade}: <strong style={{ color: 'var(--g)' }}>R$ {custoUnit.toFixed(6)}</strong>
          <span style={{ color: 'var(--c4)', marginLeft: 8, fontSize: 11 }}>
            (R$ {(custoUnit * 100).toFixed(4)} por 100{unidade})
          </span>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn1" type="submit" disabled={saving || !nome.trim()}>
          {saving ? 'Salvando...' : '✅ Cadastrar ingrediente'}
        </button>
        <button className="btn btn3" type="button" onClick={onCancel}>Cancelar</button>
      </div>
    </form>
  );
}

/* ── Modal edição (só nome/unidade/peso) ────────────────────────────────── */
function EditarModal({ insumo, onSubmit, onCancel, saving }) {
  const [nome,  setNome]  = useState(insumo.nome);
  const [peso,  setPeso]  = useState(insumo.peso_embalagem ? String(insumo.peso_embalagem) : '');

  const handleSubmit = e => {
    e.preventDefault();
    onSubmit({ nome: nome.trim(), unidade: insumo.unidade, peso_embalagem: Number(peso) || 0 });
  };

  return (
    <div className="ov" onClick={onCancel}>
      <div className="mo" onClick={e => e.stopPropagation()}>
        <div className="mo-handle" />
        <h3>✎ Editar ingrediente</h3>
        <form className="fm" onSubmit={handleSubmit}>
          <input className="inp" placeholder="Nome *" value={nome} onChange={e => setNome(e.target.value)} required autoFocus />
          <div style={{ fontSize: 12, color: 'var(--c4)' }}>
            Unidade: <strong>{insumo.unidade}</strong> (não pode ser alterada)
          </div>
          <input className="inp" type="number" step="0.001" min="0"
            placeholder={`Peso da embalagem (${insumo.unidade})`}
            value={peso} onChange={e => setPeso(e.target.value)} />
          <div className="btn-g">
            <button className="btn btn1" type="submit" disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</button>
            <button className="btn btn3" type="button" onClick={onCancel}>Cancelar</button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── Modal nova compra (atualiza preço) ─────────────────────────────────── */
function NovaCompraModal({ insumo, onConfirm, onCancel, saving }) {
  const [peso,  setPeso]  = useState(insumo.peso_embalagem ? String(insumo.peso_embalagem) : '');
  const [preco, setPreco] = useState('');
  const [qtd,   setQtd]   = useState('');

  const custoUnit = peso && preco && Number(peso) > 0
    ? Number(preco) / Number(peso)
    : null;

  const handleSubmit = e => {
    e.preventDefault();
    if (!peso || !preco || !qtd) return;
    onConfirm({ quantidade: Number(qtd), preco_pago: Number(preco), peso_lote: Number(peso) });
  };

  return (
    <div className="ov" onClick={onCancel}>
      <div className="mo" onClick={e => e.stopPropagation()}>
        <div className="mo-handle" />
        <h3>🛒 Nova compra: {insumo.nome}</h3>
        <div className="mo-sub">Atualiza o custo usado no cálculo das fichas técnicas</div>
        <form className="fm" onSubmit={handleSubmit}>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 12, color: 'var(--c4)', display: 'block', marginBottom: 3 }}>
                Peso da embalagem ({insumo.unidade})
              </label>
              <input className="inp" type="number" step="0.001" min="0.001" required
                placeholder="Ex: 1000" value={peso} onChange={e => setPeso(e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 12, color: 'var(--c4)', display: 'block', marginBottom: 3 }}>
                Preço pago (R$)
              </label>
              <input className="inp" type="number" step="0.01" min="0.01" required
                placeholder="Ex: 12,90" value={preco} onChange={e => setPreco(e.target.value)} autoFocus />
            </div>
          </div>
          <div>
            <label style={{ fontSize: 12, color: 'var(--c4)', display: 'block', marginBottom: 3 }}>
              Quantidade que entrou no estoque ({insumo.unidade})
            </label>
            <input className="inp" type="number" step="0.001" min="0.001" required
              placeholder={`Ex: ${peso || '1000'}`} value={qtd} onChange={e => setQtd(e.target.value)} />
          </div>
          {custoUnit !== null && (
            <div style={{ padding: '8px 12px', background: 'var(--g4)', borderRadius: 10, fontSize: 13 }}>
              Novo custo: <strong style={{ color: 'var(--g)' }}>R$ {custoUnit.toFixed(6)}/{insumo.unidade}</strong>
              {insumo.custo_unitario > 0 && (
                <span style={{ color: custoUnit > Number(insumo.custo_unitario) ? 'var(--r)' : 'var(--g)', marginLeft: 8, fontSize: 11 }}>
                  {custoUnit > Number(insumo.custo_unitario) ? '▲ subiu' : '▼ caiu'} vs anterior
                </span>
              )}
            </div>
          )}
          <div className="btn-g">
            <button className="btn btn1" type="submit" disabled={saving}>{saving ? 'Salvando...' : '✅ Registrar compra'}</button>
            <button className="btn btn3" type="button" onClick={onCancel}>Cancelar</button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── Página principal de Ingredientes ───────────────────────────────────── */
export default function InsumosPage({ insumos: insumosProp, onToast, onReload }) {
  const [insumos,      setInsumos]      = useState(insumosProp || []);
  const [saving,       setSaving]       = useState(false);
  const [busca,        setBusca]        = useState('');
  const [showForm,     setShowForm]     = useState(false);
  const [editando,     setEditando]     = useState(null);
  const [compraModal,  setCompraModal]  = useState(null);

  const load = async () => {
    try { const d = await getInsumos(); setInsumos(d); } catch {}
  };
  useEffect(() => { if (!insumosProp?.length) load(); }, []);
  useEffect(() => { if (insumosProp) setInsumos(insumosProp); }, [insumosProp]);

  const doSave = async (fn, msg) => {
    setSaving(true);
    try {
      await fn();
      (onToast || (() => {}))(msg);
      await load();
      if (onReload) onReload();
    } catch (err) {
      (onToast || (() => {}))(err.message, 'error');
    }
    setSaving(false);
  };

  const onCreate = async ({ nome, unidade, peso_embalagem, preco_pago }) => {
    setSaving(true);
    try {
      const ins = await createInsumo({ nome, unidade, peso_embalagem });
      await entradaInsumo(ins.id, {
        quantidade:  peso_embalagem,
        preco_pago,
        peso_lote:   peso_embalagem,
        observacao:  'Cadastro inicial',
      });
      (onToast || (() => {}))('Ingrediente cadastrado com preço!');
      await load();
      if (onReload) onReload();
      setShowForm(false);
    } catch (err) {
      (onToast || (() => {}))(err.message, 'error');
    }
    setSaving(false);
  };

  const onEdit   = (id, d) => doSave(() => updateInsumo(id, d), 'Ingrediente atualizado');
  const onDelete = i => doSave(() => deleteInsumo(i.id), 'Ingrediente removido');
  const onCompra = (id, d) => doSave(() => entradaInsumo(id, d), 'Compra registrada! Custo atualizado.');

  const lista = useMemo(() =>
    insumos.filter(i => !busca.trim() || i.nome.toLowerCase().includes(busca.toLowerCase())),
    [insumos, busca]
  );

  return (
    <div>
      <div className="c-h" style={{ marginBottom: 12 }}>
        <span className="c-t">Ingredientes</span>
        {!showForm && (
          <button className="btn btn1 btn-sm" onClick={() => setShowForm(true)}>
            + Novo ingrediente
          </button>
        )}
      </div>

      {showForm && (
        <div className="c" style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 14 }}>🧂 Novo ingrediente</div>
          <InsumoForm saving={saving} onSubmit={onCreate} onCancel={() => setShowForm(false)} />
        </div>
      )}

      {!showForm && (
        <div style={{ position: 'relative', marginBottom: 12 }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>🔍</span>
          <input className="inp" style={{ paddingLeft: 36 }} placeholder="Buscar ingrediente..." value={busca} onChange={e => setBusca(e.target.value)} />
        </div>
      )}

      {lista.length === 0 && !showForm ? (
        <div className="mt">
          <span className="mt-ico">🧂</span>
          {insumos.length === 0
            ? 'Nenhum ingrediente cadastrado. Clique em "+ Novo ingrediente" para começar.'
            : 'Nenhum resultado.'}
        </div>
      ) : (
        <div className="lst">
          {lista.map(i => {
            const temPreco = Number(i.custo_unitario) > 0;
            return (
              <div key={i.id} className="it">
                <div className="it-main">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <strong style={{ fontSize: 13 }}>{i.nome}</strong>
                    <span className="bd bd-g">{i.unidade}</span>
                    {!temPreco && (
                      <span className="bd" style={{ background: 'var(--y3,#fefce8)', color: 'var(--y,#ca8a04)', border: '1px solid var(--y,#ca8a04)' }}>
                        sem preço
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--c4)', marginTop: 3 }}>
                    {temPreco
                      ? <>Custo: <strong style={{ color: 'var(--c4)' }}>R$ {Number(i.custo_unitario).toFixed(4)}/{i.unidade}</strong>
                          {i.peso_embalagem > 0 && <> · Emb: {fmt(Number(i.custo_unitario) * i.peso_embalagem)}</>}
                        </>
                      : 'Registre uma compra para definir o custo'
                    }
                    {Number(i.estoque_atual) > 0 && <> · Estoque: {Number(i.estoque_atual).toFixed(0)}{i.unidade}</>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button
                    className="btn btn1 btn-sm"
                    onClick={() => setCompraModal(i)}
                    title="Registrar nova compra e atualizar preço"
                  >
                    🛒 Compra
                  </button>
                  <button className="btn btn3 btn-sm" onClick={() => setEditando(i)}>✎</button>
                  <button
                    className="btn btn-sm"
                    style={{ background: 'var(--r3)', color: 'var(--r)', border: '1px solid #fecaca' }}
                    onClick={() => onDelete(i)}
                  >✕</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editando && (
        <EditarModal
          insumo={editando}
          saving={saving}
          onSubmit={async d => { await onEdit(editando.id, d); setEditando(null); }}
          onCancel={() => setEditando(null)}
        />
      )}

      {compraModal && (
        <NovaCompraModal
          insumo={compraModal}
          saving={saving}
          onConfirm={async d => { await onCompra(compraModal.id, d); setCompraModal(null); }}
          onCancel={() => setCompraModal(null)}
        />
      )}
    </div>
  );
}
